import { ComponentRef, Directive, inject, OnDestroy, OnInit, output, Type, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AppRoute,
  ConfigSettingsService,
  Deferred,
  EinreichungComponents,
  FirstBelegMandatoryConfiguration,
  LogService,
} from '@ebeihilfe/core';
import { Subject, takeUntil } from 'rxjs';

import { EinreichungStep } from '../enums';
import { AntragChangeAwareComponent, EinreichungStepResultType } from '../interfaces';
import { EinreichungService } from '../services';
import { Antrag, EINREICHUNG_COMPONENT, EinreichungComponentType } from '../types';

const INITIAL_STEP_INDEX = 0;

@Directive({
  selector: '[einreichung]',
})
export class EinreichungDirective implements OnInit, OnDestroy {
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly configService = inject(ConfigSettingsService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly einreichungService = inject(EinreichungService);
  private readonly steps = inject<EinreichungComponentType[]>(EINREICHUNG_COMPONENT);
  private readonly destroyed$ = new Subject<void>();

  public antragChangeEmitter = output<Antrag>();
  public einreichungStepEmitter = output<EinreichungStep>();
  public hasUiEmitter = output<boolean>();
  private _firstBelegMandatoryConfig: FirstBelegMandatoryConfiguration = {
    isActive: false,
    mandatoryPages: 0,
  };

  public ngOnInit(): void {
    this._firstBelegMandatoryConfig = this.configService.appSettings.customizing.firstBelegMandatoryConfig;
    this.activatedRoute.params.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.startStepsFlow();
    });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private createComponent<TC>(componentType: Type<TC>): ComponentRef<TC> {
    return this.viewContainerRef.createComponent<TC>(componentType);
  }

  private async performEinreichung(): Promise<void> {
    let antrag = new Antrag(this._firstBelegMandatoryConfig);

    this.antragChangeEmitter.emit(antrag);

    const sortedSteps = this.sortSteps();
    const antragId = this.activatedRoute.snapshot.params['antragId'];

    let stepIndex = INITIAL_STEP_INDEX;
    let navigationTarget = '';

    LogService.info(`sortedSteps:${sortedSteps.map((value, index) => `\n${index} - ${value.name}`)}`);

    while (stepIndex < sortedSteps.length) {
      const step = sortedSteps[stepIndex];

      try {
        const canHandleStep = await step.canHandle(antrag, antragId);

        if (!canHandleStep) {
          stepIndex++;
          continue;
        }

        this.einreichungStepEmitter.emit(step.prio);
        this.hasUiEmitter.emit(step.hasUi());

        const result = await this.processStep(step, antrag);
        this.antragChangeEmitter.emit(antrag);

        await step.destroy();

        if (result.shouldProceed) {
          if (!result.result) {
            LogService.error('Step result missing Antrag, navigating to Dashboard');
            navigationTarget = AppRoute.DashboardPage;
            break;
          }
          antrag = result.result;
          stepIndex++;
          continue;
        }

        if (result.goBack) {
          const goTo = await EinreichungDirective.getPreviousUiStep(antrag, sortedSteps, stepIndex);
          if (goTo === INITIAL_STEP_INDEX) {
            navigationTarget = AppRoute.DashboardPage;
            break;
          }
          stepIndex = goTo;
          continue;
        }

        if (result.goTo) {
          const targetIndex = EinreichungDirective.getStepIndex(sortedSteps, result.goTo);
          if (targetIndex === INITIAL_STEP_INDEX) {
            navigationTarget = AppRoute.DashboardPage;
          } else {
            stepIndex = targetIndex;
            continue;
          }
        }

        navigationTarget = result.navigateTo || '';
        break;
      } catch (error) {
        LogService.error(`Error in performEinreichung (${step.name}):, ${JSON.stringify(error)}`);
        navigationTarget = AppRoute.DashboardPage;
        break;
      }
    }

    if (navigationTarget) {
      antrag.setComplete();
      await this.router.navigateByUrl(navigationTarget);
    }
  }

  private async startStepsFlow(): Promise<void> {
    this.viewContainerRef.clear();
    await this.performEinreichung();
  }

  private async processStep(step: EinreichungComponentType, antrag: Antrag): Promise<EinreichungStepResultType> {
    const work = new Deferred<EinreichungStepResultType>();

    const jumpSub = this.einreichungService.jumpToUpload$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      antrag.doNotFinishMe = true;
      work.resolve({ goTo: EinreichungComponents.Upload, shouldProceed: false, result: antrag });
    });

    const instance = this.createComponent(step.component).instance;
    if (this.isAntragChangeAware(instance)) {
      instance.onAntragChanged = (updatedAntrag: Antrag) => {
        this.antragChangeEmitter.emit(updatedAntrag);
      };
    }
    try {
      return await step.handle(antrag, instance, work);
    } finally {
      jumpSub.unsubscribe();
      this.viewContainerRef.clear();
    }
  }

  private sortSteps(): EinreichungComponentType[] {
    return this.steps.sort((a, b) => a.prio - b.prio);
  }

  /**
   * Type Guard zum Überprüfen, ob eine Instanz das Interface
   * `AntragChangeAwareComponent` implementiert.
   *
   * @param instance - Das zu prüfende Objekt.
   * @returns `true` wenn `onAntragChanged` existiert, sonst `false`.
   */
  private isAntragChangeAware(instance: unknown): instance is AntragChangeAwareComponent {
    return typeof instance === 'object' && instance !== null && 'onAntragChanged' in instance;
  }

  private static async getPreviousUiStep(
    antrag: Antrag,
    sortedSteps: EinreichungComponentType[],
    current: number,
  ): Promise<number> {
    for (let i = current - 1; i !== INITIAL_STEP_INDEX; i--) {
      if (sortedSteps[i].hasUi() && (await sortedSteps[i].canHandle(antrag))) {
        return i;
      }
    }
    return INITIAL_STEP_INDEX;
  }

  private static getStepIndex(sortedSteps: EinreichungComponentType[], stepName: string): number {
    if (!stepName) {
      return INITIAL_STEP_INDEX;
    }
    const stepIndex = sortedSteps.findIndex((step: EinreichungComponentType) => step.name === stepName);

    if (stepIndex !== -1 && sortedSteps[stepIndex].hasUi()) {
      return stepIndex;
    }

    return INITIAL_STEP_INDEX;
  }
}
